import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useTagadaContext } from '../providers/TagadaProvider';

export interface UseCheckoutTokenOptions {
    checkoutToken?: string;
    autoLoadFromToken?: boolean;
    autoLoadFromQueryCache?: boolean; // Try to load from React Query cache if available
}

export interface UseCheckoutTokenResult {
    checkoutToken: string | null;
    isLoading: boolean;
    error: Error | null;
    isInitialized: boolean;
    setToken: (token: string | null) => void;
    clearToken: () => void;
}

/**
 * React hook for managing checkout token state
 * Handles token management without fetching checkout data
 */
export function useCheckoutToken(options: UseCheckoutTokenOptions = {}): UseCheckoutTokenResult {
    const { isSessionInitialized } = useTagadaContext();
    const { checkoutToken: providedToken, autoLoadFromToken = true, autoLoadFromQueryCache = true } = options;
    const queryClient = useQueryClient();

    const [checkoutToken, setCheckoutToken] = useState<string | null>(providedToken || null);
    const [isLoading, _setIsLoading] = useState(false);
    const [error, setError] = useState<Error | null>(null);
    const [isInitialized, setIsInitialized] = useState(false);

    const hasAutoLoadedRef = useRef(false);
    const hasAutoLoadedFromCacheRef = useRef(false);
    const isSessionInitializedRef = useRef(isSessionInitialized);
    const checkoutTokenRef = useRef(checkoutToken);

    // Keep refs in sync with state
    useEffect(() => {
        isSessionInitializedRef.current = isSessionInitialized;
        checkoutTokenRef.current = checkoutToken;
    }, [isSessionInitialized, checkoutToken]);

    // Update token when provided token changes
    useEffect(() => {
        if (providedToken && providedToken !== checkoutToken) {
            setCheckoutToken(providedToken);
            setIsInitialized(false);
            hasAutoLoadedRef.current = false;
            hasAutoLoadedFromCacheRef.current = false;
        }
    }, [providedToken, checkoutToken]);

    // Helper function to extract token from checkout queries in cache
    const getTokenFromCache = useCallback((): string | null => {
        try {
            const queryCache = queryClient.getQueryCache();
            const checkoutQueries = queryCache.findAll({ queryKey: ['checkout'] });

            // Find the first query with a valid token in the query key
            for (const query of checkoutQueries) {
                const queryKey = query.queryKey;
                if (Array.isArray(queryKey) && queryKey.length >= 2 && typeof queryKey[1] === 'string' && queryKey[1]) {
                    const cachedToken = queryKey[1];
                    if (cachedToken) {
                        return cachedToken;
                    }
                }
            }
        } catch (err) {
            // Silently fail if query cache access fails
            console.debug('Failed to load checkoutToken from query cache:', err);
        }
        return null;
    }, [queryClient]);

    // Auto-load token from React Query cache if available (from useCheckoutQuery)
    // This runs initially and also reactively when cache changes
    useEffect(() => {
        if (!providedToken && autoLoadFromQueryCache) {
            const cachedToken = getTokenFromCache();

            // Update token if we found one in cache and don't have one yet
            if (cachedToken && cachedToken !== checkoutToken) {
                setCheckoutToken(cachedToken);
                hasAutoLoadedFromCacheRef.current = true;
            }
        }
    }, [providedToken, autoLoadFromQueryCache, checkoutToken, getTokenFromCache]);

    // Subscribe to query cache changes to reactively pick up new checkout tokens
    useEffect(() => {
        if (!providedToken && autoLoadFromQueryCache) {
            const queryCache = queryClient.getQueryCache();

            const unsubscribe = queryCache.subscribe((event) => {
                // When a query is added or updated, check if it's a checkout query
                if (event?.type === 'added' || event?.type === 'updated') {
                    const query = event.query;
                    const queryKey = query?.queryKey;

                    // Check if this is a checkout query with a token
                    // Use ref to get latest token value without causing re-subscriptions
                    if (
                        Array.isArray(queryKey) &&
                        queryKey[0] === 'checkout' &&
                        queryKey.length >= 2 &&
                        typeof queryKey[1] === 'string' &&
                        queryKey[1] &&
                        queryKey[1] !== checkoutTokenRef.current
                    ) {
                        const newToken = queryKey[1];
                        setCheckoutToken(newToken);
                        hasAutoLoadedFromCacheRef.current = true;
                    }
                }
            });

            return () => {
                unsubscribe();
            };
        }
    }, [providedToken, autoLoadFromQueryCache, queryClient]);

    // Auto-load token from URL if no token provided
    useEffect(() => {
        if (!providedToken && autoLoadFromToken && !checkoutToken && !hasAutoLoadedRef.current) {
            const urlParams = new URLSearchParams(window.location.search);
            const urlToken = urlParams.get('checkoutToken') || urlParams.get('token');

            if (urlToken) {
                setCheckoutToken(urlToken);
                hasAutoLoadedRef.current = true;
            }
        }
    }, [providedToken, autoLoadFromToken, checkoutToken]);

    // Wait for session initialization
    useEffect(() => {
        if (!isSessionInitialized || !checkoutToken || isInitialized) {
            return;
        }


        setIsInitialized(true);
    }, [isSessionInitialized, checkoutToken, isInitialized]);

    const setToken = useCallback((token: string | null) => {
        setCheckoutToken(token);
        setIsInitialized(!!token);
        setError(null);
    }, []);

    const clearToken = useCallback(() => {
        setCheckoutToken(null);
        setIsInitialized(false);
        setError(null);
        hasAutoLoadedRef.current = false;
        hasAutoLoadedFromCacheRef.current = false;
    }, []);

    return {
        checkoutToken,
        isLoading,
        error,
        isInitialized,
        setToken,
        clearToken,
    };
}
